<template>
  <TableComp
    :headers="headers"
    :name="$t('global.magento-patches-available-ported')"
    :status="status"
    :number-of-items="getList().length"
    empty-image="magento-patches"
    :empty-bold-message="$t('global.no-magento-patches-bold-message')"
    :empty-message="$t('global.no-magento-patches-message')"
  >
    <template #content>
      <div class="mb-4">
        <CardListContent :list="getInfoList()" />
        <CardActionContent :buttons="getButtons()">
          <template #description>
            <p>{{ $t("global.magento-patches-description-2") }}</p>
            <a :href="KB_LINK" target="_blank">
              {{ $t("global.magento-patches-apply-read-more") }}
            </a>
          </template>
        </CardActionContent>
      </div>
    </template>
    <template v-slot:desktop>
      <TableRow v-for="(patch, index) in getList()" :key="index">
        <td class="align-middle">{{ patch.name }}</td>
        <td class="align-middle text-center">{{ patch.date }}</td>
        <template #actionItems>
          <FormCheckbox v-model="selected.checkbox[patch.name]" class="mr-4" />
          <ActionIcons :actions="getPatchActions(patch)" />
        </template>
      </TableRow>
    </template>
    <template v-slot:mobile>
      <TableMobileRow v-for="(patch, index) in getList()" v-bind:key="index">
        <template v-slot:header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              {{ patch.name }}
            </div>
            <div class="d-flex">
              <FormCheckbox v-model="selected.checkbox[patch.name]" class="mr-4" />
              <ActionIcons :actions="getPatchActions(patch)" />
            </div>
          </div>
        </template>
        <template v-slot:content>
          <div class="row">
            <div class="col">{{ $t("global.date") }}:</div>
            <div class="col">{{ patch.date }}</div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import ActionIcons from "@/components/display/ActionIcons";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import ContactSupport from "@/components/smart/modals/ContactSupport";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import NavLink from "@/classes/NavLink";
import { reactive } from "@vue/composition-api";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import TableRow from "@/components/display/table/TableRow";

export default {
  name: "MagentoPatchesTable",
  props: {
    patches: Object,
    service: {
      type: Object,
      required: true,
    },
    status: {
      type: Object,
      require: true,
    },
  },
  components: {
    ActionIcons,
    CardActionContent,
    CardListContent,
    FormCheckbox,
    TableComp,
    TableMobileRow,
    TableRow,
  },
  setup(props, { root }) {
    const KB_LINK = "https://help.nexcess.net/magento/how-to-patch-your-magento-1-store";
    const selected = reactive({ checkbox: {} });
    const headers = [
      new TableHeader(root.$t("global.name"), undefined),
      new TableHeader(root.$t("global.date"), "justify-content-center"),
      new TableHeader(),
    ];

    const getButtons = () => [
      new NavLink(
        root.$t("global.request-installation"),
        null,
        openRequestInstallationForm,
        false,
        "primary",
        "epsilon"
      ),
    ];

    const getInfoList = () => {
      if (props.status.isLoaded || props.status.isUpdating) {
        return [
          {
            label: root.$t("global.version"),
            content: props.patches?.version,
            canCopy: true,
          },
        ];
      }
      return [];
    };

    const getList = () => props.patches?.patches || [];

    const getPatchActions = (patch) => [
      new NavLink(
        root.$t("global.view"),
        "open-page",
        () => window.open(patch.url, "_blank"),
        false,
        null,
        "epsilon"
      ),
      new NavLink(
        root.$t("global.download"),
        "download",
        () => window.open(patch.download),
        false,
        null,
        "epsilon"
      ),
    ];

    const getSelectedPatches = () => {
      return getList().filter((item) => selected.checkbox[item.name]);
    };

    const getTicketBody = () =>
      getSelectedPatches().reduce(
        (acc, patch) => acc + `${patch.date}\n${patch.name}\n${patch.url}\n${patch.download}\n\n`,
        `${root.$t("global.please-install-magento-patches")}\n\n`
      );

    const openRequestInstallationForm = () =>
      store.dispatch("modals/open", {
        template: ContactSupport,
        data: {
          serviceId: props.service.id,
          subject: root.$t("global.request-magento-patch-installation"),
          reply: getTicketBody(),
          department: root.$t("global.support"),
        },
      });

    return {
      getButtons,
      getInfoList,
      getList,
      getPatchActions,
      getTicketBody,
      headers,
      KB_LINK,
      selected,
    };
  },
};
</script>
